import { useIntl } from 'react-intl';

import { useAppSelector } from '@/shared/hooks';

import { composedModel } from '@/entities/composed';
import {
  organizationModel,
  STORAGE_FULL_THRESHOLD,
  STORAGE_WARNING_THRESHOLD,
} from '@/entities/organization';
import { userModel } from '@/entities/user';

import { BANNER_STORAGE_KEYS, StorageBannerProps } from '../config';

export const useStorageBanners = () => {
  const { formatMessage } = useIntl();

  const isStorageFeatureReady = useAppSelector(
    composedModel.selectors.selectIsStorageFeatureReady,
  );

  const isStorageWarning = useAppSelector(
    composedModel.selectors.selectIsStorageWarning,
  );

  const isStorageFull = useAppSelector(
    composedModel.selectors.selectIsStorageFull,
  );

  const currentUserID = useAppSelector(userModel.selectors.selectCurrentUserID);
  const isCurrentUserOwner = useAppSelector(
    organizationModel.selectors.selectIsUserOwner(currentUserID ?? ''),
  );

  const shouldShowStorageWarningBanner =
    isStorageFeatureReady && isStorageWarning && isCurrentUserOwner;
  const shouldShowStorageFullBanner = isStorageFeatureReady && isStorageFull;

  const getStorageBannerProps = (): StorageBannerProps | null => {
    if (shouldShowStorageFullBanner) {
      return {
        variant: 'fatal',
        closable: false,
        message: formatMessage(
          {
            id: 'storage.warningMessage',
            defaultMessage:
              "You've reached {percentage}% of your storage limit. Please contact your sales manager to extend the limit.",
          },
          { percentage: STORAGE_FULL_THRESHOLD },
        ),
      };
    }

    if (shouldShowStorageWarningBanner) {
      return {
        variant: 'warning',
        closable: true,
        storageKey: BANNER_STORAGE_KEYS.storageWarning,
        message: formatMessage(
          {
            id: 'storage.warningMessage',
            defaultMessage:
              "You've reached {percentage}% of your storage limit. Please contact your sales manager to extend the limit.",
          },
          { percentage: STORAGE_WARNING_THRESHOLD },
        ),
      };
    }

    return null;
  };

  return {
    shouldShowStorageBanner:
      shouldShowStorageWarningBanner || shouldShowStorageFullBanner,
    storageBannerProps: getStorageBannerProps(),
  };
};
